/**
 * Admin → Pricing (Maker-only)
 * ────────────────────────────────────────────────────────────────
 * Maker controls every monetary surface of the platform:
 *   1. Subscription Plans — Monthly / Annual / Trial pricing
 *   2. Modules            — 12 factory modules, edit price or set FREE
 *   3. Custom Modules     — Add brand-new revenue line items
 *   4. Per-Tenant Overrides — Custom rates for individual tenants
 *
 * Backend gating: is_maker(user) — only ceo@toothrocket.com (or whoever
 * MAKER_EMAIL points to) gets through the API; the frontend renders an
 * access notice for everyone else.
 *
 * Visual language: Palantir Gotham / Bloomberg Terminal — bracketed
 * headers, monospace data readouts, sharp 1px borders, ASCII fill bars.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DollarSign, Plus, RefreshCw, Trash2, Gift, Package, Building2,
  Tag, Sparkles, AlertTriangle, X, Save, RotateCcw, Edit3,
  TrendingUp, BarChart3,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';

const TABS = [
  { id: 'revenue',  label: 'Revenue · ARR',      icon: TrendingUp, testid: 'pricing-tab-revenue' },
  { id: 'plans',    label: 'Subscription Plans', icon: Tag,        testid: 'pricing-tab-plans' },
  { id: 'modules',  label: 'Modules',            icon: Package,    testid: 'pricing-tab-modules' },
  { id: 'custom',   label: 'Custom Modules',     icon: Plus,       testid: 'pricing-tab-custom' },
  { id: 'tenants',  label: 'Per-Tenant Overrides', icon: Building2, testid: 'pricing-tab-tenants' },
];

function fmtQar(n) {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })} QAR`;
}

function FreeBadge({ testid = 'pricing-free-badge' }) {
  return (
    <span
      className="pal-mono text-[9px] tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm border"
      style={{
        backgroundColor: 'rgba(16,185,129,0.10)',
        color: '#10B981',
        borderColor: 'rgba(16,185,129,0.40)',
      }}
      data-testid={testid}
    >
      FREE
    </span>
  );
}

function OverrideBadge({ scope = 'GLOBAL', testid }) {
  return (
    <span
      className="pal-mono text-[9px] tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm border"
      style={{
        backgroundColor: 'rgba(174,82,234,0.10)',
        color: '#C084FC',
        borderColor: 'rgba(174,82,234,0.40)',
      }}
      data-testid={testid}
    >
      {scope} OVERRIDE
    </span>
  );
}

export default function AdminPricing() {
  const { effective } = useTheme();
  const { user } = useAuth();
  const isDark = effective === 'dark';
  const isMaker = !!user?.is_maker;

  const [tab, setTab] = useState('revenue');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantData, setTenantData] = useState(null);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive */
  const refresh = async () => {
    setLoading(true);
    try {
      const [p, m, t] = await Promise.all([
        api.get('/billing/admin/plans'),
        api.get('/billing/admin/modules'),
        api.get('/billing/admin/tenants'),
      ]);
      setPlans(p.data.plans || []);
      setModules(m.data.modules || []);
      setTenants(t.data.tenants || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isMaker) refresh(); else setLoading(false); }, [isMaker]);

  useEffect(() => {
    if (!selectedTenant) { setTenantData(null); return; }
    api.get(`/billing/admin/tenant-overrides/${selectedTenant}`)
      .then(({ data }) => setTenantData(data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load tenant overrides'));
  }, [selectedTenant]);
  /* eslint-enable */

  if (!isMaker) {
    return (
      <div className="space-y-5" data-testid="admin-pricing-page">
        <div className="pal-card pal-grid-bg p-6 max-w-2xl">
          <div className="flex items-start gap-4">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center shrink-0 border"
              style={{
                backgroundColor: 'rgba(250,176,5,0.10)',
                color: '#F59E0B',
                borderColor: 'rgba(250,176,5,0.40)',
              }}
            >
              <AlertTriangle size={22} />
            </div>
            <div className="flex-1">
              <div className="pal-bracket">[MAKER · PRICING]</div>
              <h1 className={`display text-2xl mt-0.5 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
                Maker-only console
              </h1>
              <p className="text-sm mt-2 opacity-70">
                Pricing controls are restricted to the platform Maker. Reach out to
                your Maker if you need to adjust subscription plans, module pricing,
                or per-tenant overrides.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-pricing-page">
      {/* ── Header ── */}
      <div className="pal-card pal-grid-bg p-5 flex items-start gap-4">
        <div
          className="h-12 w-12 rounded-md flex items-center justify-center shrink-0 border"
          style={{
            backgroundColor: 'rgba(212,175,55,0.10)',
            color: '#D4AF37',
            borderColor: 'rgba(212,175,55,0.40)',
          }}
        >
          <DollarSign size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="pal-bracket">[MAKER · PRICING]</div>
          <h1 className={`display text-2xl mt-0.5 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
            Pricing &amp; Monetization
          </h1>
          <p className="text-sm mt-1 max-w-2xl opacity-70">
            Edit subscription plans, module prices, create custom modules, and set
            per-tenant overrides. Changes are LIVE — the public Pricing page reflects
            them instantly. Set anything to FREE to remove paywalls.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          data-testid="pricing-refresh"
          className={`px-3 py-2 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold ${
            isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          } disabled:opacity-50 flex items-center gap-1.5`}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          REFRESH
        </button>
      </div>

      {/* ── Tab bar ── */}
      <div className="flex flex-wrap gap-1.5" data-testid="pricing-tabs">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={tb.testid}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold rounded-md border pal-mono flex items-center gap-2 transition-colors ${
                active
                  ? 'bg-[rgba(174,82,234,0.12)] border-[rgba(174,82,234,0.50)] text-[var(--ve-violet-bright)]'
                  : isDark
                    ? 'border-white/10 text-slate-400 hover:bg-white/5'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Icon size={12} />
              {tb.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="pal-card p-8 text-center opacity-60 text-sm">Loading pricing data…</div>
      ) : (
        <>
          {tab === 'revenue' && <RevenueTab isDark={isDark} />}
          {tab === 'plans'   && <PlansTab plans={plans} onChange={refresh} isDark={isDark} />}
          {tab === 'modules' && <ModulesTab modules={modules} onChange={refresh} isDark={isDark} />}
          {tab === 'custom'  && <CustomTab modules={modules} onChange={refresh} isDark={isDark} />}
          {tab === 'tenants' && (
            <TenantsTab
              tenants={tenants}
              selectedTenant={selectedTenant}
              setSelectedTenant={setSelectedTenant}
              tenantData={tenantData}
              onChange={async () => {
                await refresh();
                if (selectedTenant) {
                  const { data } = await api.get(`/billing/admin/tenant-overrides/${selectedTenant}`);
                  setTenantData(data);
                }
              }}
              isDark={isDark}
            />
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//                        SUBSCRIPTION PLANS
// ────────────────────────────────────────────────────────────────
function PlansTab({ plans, onChange, isDark }) {
  return (
    <div className="pal-card pal-grid-bg p-5" data-testid="pricing-plans-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag size={13} className="text-[var(--ve-violet-bright)]" />
          <span className="pal-bracket">[SUBSCRIPTION · PLANS]</span>
        </div>
        <span className="pal-mono text-[10px] opacity-60">{plans.length} PLANS</span>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} onChange={onChange} isDark={isDark} />
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan, onChange, isDark }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(plan.amount_qar);
  const [name, setName] = useState(plan.name);
  const [tagline, setTagline] = useState(plan.tagline || '');
  const [busy, setBusy] = useState(false);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = {
        amount_qar: Number(amount),
        name,
        tagline,
        ...extra,
      };
      await api.patch(`/billing/admin/plans/${plan.id}`, body);
      toast.success(`Plan "${plan.id}" updated.`);
      setEditing(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleFree = () => save({ free: !plan.free });
  const reset = async () => {
    setBusy(true);
    try {
      await api.patch(`/billing/admin/plans/${plan.id}`, { reset: true });
      toast.success(`Plan "${plan.id}" reset to factory.`);
      setEditing(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-4 ${
        isDark ? 'bg-[rgba(174,82,234,0.03)] border-white/10' : 'bg-slate-50 border-slate-200'
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          {editing ? (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`text-base font-bold bg-transparent border-b ${
                isDark ? 'border-white/20 text-white' : 'border-slate-300'
              } focus:outline-none focus:border-[var(--ve-violet-bright)] pal-mono`}
              data-testid={`plan-${plan.id}-name-input`}
            />
          ) : (
            <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {plan.name}
            </h3>
          )}
          <span className="pal-mono text-[10px] uppercase tracking-[0.18em] opacity-50">
            {plan.id} · {plan.billing}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1">
          {plan.free && <FreeBadge testid={`plan-${plan.id}-free-badge`} />}
          {plan.override_set && <OverrideBadge testid={`plan-${plan.id}-override-badge`} />}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <div>
            <label className="pal-bracket text-[9px]">[AMOUNT · QAR]</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="100"
              data-testid={`plan-${plan.id}-amount-input`}
              className={`mt-1 w-full px-2 py-1.5 text-sm rounded border pal-mono ${
                isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
              } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
            />
          </div>
          <div>
            <label className="pal-bracket text-[9px]">[TAGLINE]</label>
            <textarea
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              rows={2}
              data-testid={`plan-${plan.id}-tagline-input`}
              className={`mt-1 w-full px-2 py-1.5 text-xs rounded border pal-mono ${
                isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
              } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="display text-2xl mb-1" style={{ color: '#D4AF37' }}>
            {plan.free ? 'FREE' : fmtQar(plan.amount_qar)}
            {!plan.free && (
              <span className="text-xs opacity-50 ml-1">/ {plan.billing}</span>
            )}
          </div>
          <p className="text-[11px] opacity-60 leading-snug mb-2">
            {plan.tagline || '—'}
          </p>
          {plan.override_set && (
            <div className="text-[9px] opacity-50 pal-mono mb-2">
              Factory: {fmtQar(plan.factory_amount_qar)} · by {plan.updated_by || '—'}
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-dashed border-[rgba(174,82,234,0.15)]">
        {editing ? (
          <>
            <button
              onClick={() => save()}
              disabled={busy}
              data-testid={`plan-${plan.id}-save`}
              className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20 flex items-center gap-1 disabled:opacity-50"
            >
              <Save size={11} /> SAVE
            </button>
            <button
              onClick={() => { setEditing(false); setAmount(plan.amount_qar); setName(plan.name); setTagline(plan.tagline); }}
              disabled={busy}
              className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
                isDark ? 'border-white/10 text-slate-400 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              <X size={11} /> CANCEL
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setEditing(true)}
              data-testid={`plan-${plan.id}-edit`}
              className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.40)] hover:bg-[rgba(174,82,234,0.20)] flex items-center gap-1"
            >
              <Edit3 size={11} /> EDIT
            </button>
            <button
              onClick={toggleFree}
              disabled={busy}
              data-testid={`plan-${plan.id}-toggle-free`}
              className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
                plan.free
                  ? 'bg-amber-500/10 text-amber-500 border-amber-500/40 hover:bg-amber-500/20'
                  : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20'
              } disabled:opacity-50`}
            >
              <Gift size={11} /> {plan.free ? 'UNDO FREE' : 'SET FREE'}
            </button>
            {plan.override_set && (
              <button
                onClick={reset}
                disabled={busy}
                data-testid={`plan-${plan.id}-reset`}
                className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
                  isDark ? 'border-white/10 text-slate-400 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                } disabled:opacity-50`}
              >
                <RotateCcw size={11} /> RESET
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//                        MODULES TAB
// ────────────────────────────────────────────────────────────────
function ModulesTab({ modules, onChange, isDark }) {
  const factoryMods = modules.filter((m) => !m.custom);
  const customMods = modules.filter((m) => m.custom);

  return (
    <div className="space-y-4">
      <div className="pal-card pal-grid-bg p-5" data-testid="pricing-modules-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[FACTORY · MODULES]</span>
          </div>
          <span className="pal-mono text-[10px] opacity-60">{factoryMods.length} MODULES</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {factoryMods.map((m) => (
            <ModuleCard key={m.id} mod={m} onChange={onChange} isDark={isDark} />
          ))}
        </div>
      </div>

      {customMods.length > 0 && (
        <div className="pal-card p-5" data-testid="pricing-custom-modules-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={13} className="text-emerald-500" />
              <span className="pal-bracket">[CUSTOM · MODULES]</span>
            </div>
            <span className="pal-mono text-[10px] opacity-60">{customMods.length} CUSTOM</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {customMods.map((m) => (
              <ModuleCard key={m.id} mod={m} onChange={onChange} isDark={isDark} allowDelete />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModuleCard({ mod, onChange, isDark, allowDelete = false }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(mod.price_qar_month);
  const [busy, setBusy] = useState(false);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = { price_qar_month: Number(price), ...extra };
      await api.patch(`/billing/admin/modules/${mod.id}`, body);
      toast.success(`Module "${mod.name}" updated.`);
      setEditing(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleFree = () => save({ free: !mod.free });
  const reset = async () => {
    setBusy(true);
    try {
      await api.patch(`/billing/admin/modules/${mod.id}`, { reset: true });
      toast.success(`Module "${mod.name}" reset.`);
      setEditing(false);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const deleteCustom = async () => {
    if (!window.confirm(`Delete custom module "${mod.name}"? This is irreversible.`)) return;
    setBusy(true);
    try {
      await api.delete(`/billing/admin/modules/${mod.id}`);
      toast.success(`Custom module "${mod.name}" deleted.`);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-3.5 ${
        isDark ? 'bg-[rgba(174,82,234,0.03)] border-white/10' : 'bg-slate-50 border-slate-200'
      }`}
      data-testid={`module-card-${mod.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'} truncate`} data-testid={mod.custom ? `custom-module-row-${mod.id}` : undefined}>
            {mod.name}
          </h3>
          <p className="text-[11px] opacity-60 line-clamp-2 mt-0.5">{mod.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {mod.free && <FreeBadge testid={`module-${mod.id}-free-badge`} />}
          {mod.price_override && <OverrideBadge testid={`module-${mod.id}-override-badge`} />}
        </div>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        {editing ? (
          <input
            type="number"
            value={price}
            min="0"
            step="50"
            onChange={(e) => setPrice(e.target.value)}
            data-testid={`module-${mod.id}-price-input`}
            className={`px-2 py-1 text-sm rounded border pal-mono w-32 ${
              isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
            } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
          />
        ) : (
          <div className="display text-lg" style={{ color: '#D4AF37' }}>
            {mod.free ? 'FREE' : fmtQar(mod.price_qar_month)}
          </div>
        )}
        {!editing && !mod.free && (
          <span className="text-[10px] opacity-50">/ month</span>
        )}
      </div>
      <div className="text-[9px] opacity-50 pal-mono mb-2">
        {mod.id} · {(mod.available_for || []).join(', ')}
        {mod.price_override && ` · factory ${fmtQar(mod.factory_price_qar_month)}`}
      </div>

      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-dashed border-[rgba(174,82,234,0.15)]">
        {editing ? (
          <>
            <button
              onClick={() => save()}
              disabled={busy}
              data-testid={`module-${mod.id}-save`}
              className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20 flex items-center gap-1"
            >
              <Save size={10} /> SAVE
            </button>
            <button
              onClick={() => { setEditing(false); setPrice(mod.price_qar_month); }}
              className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
                isDark ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'
              }`}
            >
              <X size={10} />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setEditing(true)}
              data-testid={`module-${mod.id}-edit`}
              className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.40)] hover:bg-[rgba(174,82,234,0.20)] flex items-center gap-1"
            >
              <Edit3 size={10} /> EDIT
            </button>
            <button
              onClick={toggleFree}
              disabled={busy || mod.required}
              data-testid={`module-${mod.id}-toggle-free`}
              className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
                mod.free
                  ? 'bg-amber-500/10 text-amber-500 border-amber-500/40 hover:bg-amber-500/20'
                  : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              title={mod.required ? 'Required modules cannot be FREE' : ''}
            >
              <Gift size={10} /> {mod.free ? 'UNDO FREE' : 'FREE'}
            </button>
            {mod.price_override && (
              <button
                onClick={reset}
                disabled={busy}
                data-testid={`module-${mod.id}-reset`}
                className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
                  isDark ? 'border-white/10 text-slate-400 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                <RotateCcw size={10} /> RESET
              </button>
            )}
            {allowDelete && (
              <button
                onClick={deleteCustom}
                disabled={busy}
                data-testid={`custom-module-delete-${mod.id}`}
                className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-rose-500/10 text-rose-500 border-rose-500/40 hover:bg-rose-500/20 flex items-center gap-1 ml-auto"
              >
                <Trash2 size={10} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//                        CUSTOM MODULES TAB
// ────────────────────────────────────────────────────────────────
function CustomTab({ modules, onChange, isDark }) {
  const PERSONAS = ['investor', 'startup', 'holding', 'incubator'];
  const [form, setForm] = useState({
    module_id: '', name: '', description: '',
    price_qar_month: 1000, available_for: PERSONAS.slice(),
    icon: 'sparkles',
  });
  const [busy, setBusy] = useState(false);
  const customMods = modules.filter((m) => m.custom);

  const togglePersona = (p) => {
    setForm((f) => ({
      ...f,
      available_for: f.available_for.includes(p)
        ? f.available_for.filter((x) => x !== p)
        : [...f.available_for, p],
    }));
  };

  const create = async () => {
    if (!form.module_id || !form.name || !form.description) {
      toast.error('module_id, name and description are required.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/billing/admin/modules', {
        ...form,
        price_qar_month: Number(form.price_qar_month),
      });
      toast.success(`Custom module "${form.name}" created.`);
      setForm({
        module_id: '', name: '', description: '',
        price_qar_month: 1000, available_for: PERSONAS.slice(),
        icon: 'sparkles',
      });
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="pal-card pal-grid-bg p-5" data-testid="pricing-custom-create-card">
        <div className="flex items-center gap-2 mb-4">
          <Plus size={13} className="text-emerald-500" />
          <span className="pal-bracket">[CREATE · CUSTOM MODULE]</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="pal-bracket text-[9px]">[MODULE ID]</label>
            <input
              value={form.module_id}
              onChange={(e) => setForm((f) => ({ ...f, module_id: e.target.value.toLowerCase() }))}
              placeholder="e.g. crypto_treasury"
              data-testid="custom-module-id-input"
              className={`mt-1 w-full px-2 py-1.5 text-sm rounded border pal-mono ${
                isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
              } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
            />
          </div>
          <div>
            <label className="pal-bracket text-[9px]">[NAME]</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Crypto Treasury"
              data-testid="custom-module-name-input"
              className={`mt-1 w-full px-2 py-1.5 text-sm rounded border pal-mono ${
                isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
              } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
            />
          </div>
          <div className="md:col-span-2">
            <label className="pal-bracket text-[9px]">[DESCRIPTION]</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
              placeholder="One-sentence value proposition for the pricing page."
              data-testid="custom-module-description-input"
              className={`mt-1 w-full px-2 py-1.5 text-xs rounded border pal-mono ${
                isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
              } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
            />
          </div>
          <div>
            <label className="pal-bracket text-[9px]">[PRICE · QAR / MONTH]</label>
            <input
              type="number"
              value={form.price_qar_month}
              min="0"
              step="100"
              onChange={(e) => setForm((f) => ({ ...f, price_qar_month: e.target.value }))}
              data-testid="custom-module-price-input"
              className={`mt-1 w-full px-2 py-1.5 text-sm rounded border pal-mono ${
                isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
              } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
            />
          </div>
          <div>
            <label className="pal-bracket text-[9px]">[AVAILABLE FOR]</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {PERSONAS.map((p) => {
                const on = form.available_for.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePersona(p)}
                    data-testid={`custom-module-persona-${p}`}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md border pal-mono ${
                      on
                        ? 'bg-[rgba(174,82,234,0.15)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.50)]'
                        : isDark
                          ? 'border-white/10 text-slate-400 hover:bg-white/5'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <button
          onClick={create}
          disabled={busy}
          data-testid="custom-module-create-button"
          className="mt-4 px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold rounded border pal-mono bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20 disabled:opacity-50 flex items-center gap-1.5"
        >
          <Plus size={12} /> CREATE MODULE
        </button>
      </div>

      <div className="pal-card p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="pal-bracket">[YOUR · CUSTOM MODULES]</span>
          <span className="pal-mono text-[10px] opacity-60">{customMods.length}</span>
        </div>
        {customMods.length === 0 ? (
          <div className="text-sm opacity-60 italic py-4 text-center">
            No custom modules yet. Use the form above to add your first revenue line.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {customMods.map((m) => (
              <ModuleCard key={m.id} mod={m} onChange={onChange} isDark={isDark} allowDelete />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//                    PER-TENANT OVERRIDES TAB
// ────────────────────────────────────────────────────────────────
function TenantsTab({ tenants, selectedTenant, setSelectedTenant, tenantData, onChange, isDark }) {
  const filtered = useMemo(
    () => tenants.filter((t) => t.tenant_id !== 'primary'),
    [tenants],
  );

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      {/* Tenant list */}
      <div className="lg:col-span-4 pal-card p-4" data-testid="pricing-tenant-list">
        <div className="flex items-center justify-between mb-3">
          <span className="pal-bracket">[TENANTS]</span>
          <span className="pal-mono text-[10px] opacity-60">{filtered.length}</span>
        </div>
        <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((t) => {
            const isSel = selectedTenant === t.tenant_id;
            return (
              <button
                key={t.tenant_id}
                onClick={() => setSelectedTenant(t.tenant_id)}
                data-testid={`tenant-row-${t.tenant_id}`}
                className={`w-full text-left px-3 py-2 rounded border pal-mono text-[11px] transition-colors ${
                  isSel
                    ? 'bg-[rgba(174,82,234,0.15)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.50)]'
                    : isDark
                      ? 'border-white/5 text-slate-300 hover:bg-white/5'
                      : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{t.brand_name || t.tenant_id}</span>
                  {t.has_pricing_override && (
                    <span className="text-[8px] tracking-[0.16em] text-[var(--ve-violet-bright)] shrink-0">
                      ●
                    </span>
                  )}
                </div>
                <div className="text-[9px] opacity-50 truncate">{t.tenant_id}</div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-xs opacity-60 italic py-2 px-1">No tenants found.</div>
          )}
        </div>
      </div>

      {/* Override editor */}
      <div className="lg:col-span-8" data-testid="tenant-editor">
        {!selectedTenant ? (
          <div className="pal-card p-8 text-center">
            <Building2 size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm opacity-60">
              Select a tenant to set custom module or plan prices for them.
            </p>
          </div>
        ) : !tenantData ? (
          <div className="pal-card p-8 text-center opacity-60 text-sm">Loading…</div>
        ) : (
          <TenantOverrideEditor
            tenantData={tenantData}
            tenantId={selectedTenant}
            onChange={onChange}
            isDark={isDark}
          />
        )}
      </div>
    </div>
  );
}

function TenantOverrideEditor({ tenantData, tenantId, onChange, isDark }) {
  const [busyKey, setBusyKey] = useState(null);
  const t = tenantData.tenant || {};

  const setModule = async (moduleId, payload) => {
    setBusyKey(`mod:${moduleId}`);
    try {
      await api.patch(
        `/billing/admin/tenant-overrides/${tenantId}/modules/${moduleId}`,
        payload,
      );
      toast.success(`Override applied to ${moduleId}.`);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusyKey(null);
    }
  };

  const setPlan = async (planId, payload) => {
    setBusyKey(`plan:${planId}`);
    try {
      await api.patch(
        `/billing/admin/tenant-overrides/${tenantId}/plans/${planId}`,
        payload,
      );
      toast.success(`Plan override applied.`);
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusyKey(null);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Wipe ALL pricing overrides for ${t.brand_name || tenantId}?`)) return;
    setBusyKey('all');
    try {
      await api.delete(`/billing/admin/tenant-overrides/${tenantId}`);
      toast.success('All overrides cleared.');
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="pal-card pal-grid-bg p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="pal-bracket">[TENANT · OVERRIDES]</div>
            <h2 className={`display text-xl mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t.brand_name || tenantId}
            </h2>
            <p className="pal-mono text-[10px] opacity-50 mt-0.5">
              {tenantId} · last updated {tenantData.overrides?.updated_at || 'never'}
            </p>
          </div>
          <button
            onClick={clearAll}
            disabled={busyKey === 'all'}
            data-testid="tenant-clear-all"
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-rose-500/10 text-rose-500 border-rose-500/40 hover:bg-rose-500/20 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Trash2 size={11} /> CLEAR ALL
          </button>
        </div>
      </div>

      {/* Plans section */}
      <div className="pal-card p-4">
        <div className="pal-bracket mb-3">[PLANS · FOR THIS TENANT]</div>
        <div className="space-y-1.5">
          {tenantData.plans.map((p) => (
            <TenantPlanRow
              key={p.id}
              plan={p}
              busy={busyKey === `plan:${p.id}`}
              onSetPrice={(amt) => setPlan(p.id, { amount_qar: amt })}
              onSetFree={() => setPlan(p.id, { free: true })}
              onReset={() => setPlan(p.id, { reset: true })}
              isDark={isDark}
            />
          ))}
        </div>
      </div>

      {/* Modules section */}
      <div className="pal-card p-4">
        <div className="pal-bracket mb-3">[MODULES · FOR THIS TENANT]</div>
        <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-2">
          {tenantData.modules.map((m) => (
            <TenantModuleRow
              key={m.id}
              mod={m}
              busy={busyKey === `mod:${m.id}`}
              onSetPrice={(amt) => setModule(m.id, { price_qar_month: amt })}
              onSetFree={() => setModule(m.id, { free: true })}
              onReset={() => setModule(m.id, { reset: true })}
              isDark={isDark}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TenantPlanRow({ plan, busy, onSetPrice, onSetFree, onReset, isDark }) {
  const inputRef = React.useRef(null);

  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-2 rounded border ${
        isDark ? 'border-white/5 bg-[rgba(174,82,234,0.02)]' : 'border-slate-200 bg-white'
      }`}
      data-testid={`tenant-plan-row-${plan.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="pal-mono text-[11px] font-semibold">{plan.name}</div>
        <div className="pal-mono text-[9px] opacity-50">{plan.id} · {plan.billing}</div>
      </div>
      <div className="flex items-center gap-1.5">
        {plan.free && <FreeBadge />}
        {plan.tenant_override && <OverrideBadge scope="TENANT" />}
      </div>
      <input
        ref={inputRef}
        type="number"
        defaultValue={plan.amount_qar}
        key={`plan-${plan.id}-${plan.amount_qar}-${plan.tenant_override}`}
        min="0"
        step="100"
        data-testid={`tenant-plan-${plan.id}-input`}
        className={`w-28 px-2 py-1 text-xs rounded border pal-mono ${
          isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
        } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
      />
      <button
        onClick={() => onSetPrice(Number(inputRef.current?.value ?? plan.amount_qar))}
        disabled={busy}
        data-testid={`tenant-plan-${plan.id}-save`}
        className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.40)] hover:bg-[rgba(174,82,234,0.20)] disabled:opacity-40"
      >
        SET
      </button>
      <button
        onClick={onSetFree}
        disabled={busy}
        data-testid={`tenant-plan-${plan.id}-free`}
        className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20 disabled:opacity-40 flex items-center gap-1"
        title="Set FREE for this tenant"
      >
        <Gift size={10} />
      </button>
      {plan.tenant_override && (
        <button
          onClick={onReset}
          disabled={busy}
          data-testid={`tenant-plan-${plan.id}-reset`}
          className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
            isDark ? 'border-white/10 text-slate-400 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
        >
          <RotateCcw size={10} />
        </button>
      )}
    </div>
  );
}

function TenantModuleRow({ mod, busy, onSetPrice, onSetFree, onReset, isDark }) {
  const inputRef = React.useRef(null);

  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-2 rounded border ${
        isDark ? 'border-white/5 bg-[rgba(174,82,234,0.02)]' : 'border-slate-200 bg-white'
      }`}
      data-testid={`tenant-module-row-${mod.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="pal-mono text-[11px] font-semibold truncate">{mod.name}</div>
        <div className="pal-mono text-[9px] opacity-50 truncate">{mod.id}</div>
      </div>
      <div className="flex items-center gap-1.5">
        {mod.free && <FreeBadge />}
        {mod.tenant_override && <OverrideBadge scope="TENANT" />}
      </div>
      <input
        ref={inputRef}
        type="number"
        defaultValue={mod.price_qar_month}
        key={`mod-${mod.id}-${mod.price_qar_month}-${mod.tenant_override}`}
        min="0"
        step="50"
        data-testid={`tenant-module-${mod.id}-input`}
        className={`w-28 px-2 py-1 text-xs rounded border pal-mono ${
          isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'
        } focus:outline-none focus:border-[var(--ve-violet-bright)]`}
      />
      <button
        onClick={() => onSetPrice(Number(inputRef.current?.value ?? mod.price_qar_month))}
        disabled={busy}
        data-testid={`tenant-module-${mod.id}-save`}
        className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)] border-[rgba(174,82,234,0.40)] hover:bg-[rgba(174,82,234,0.20)] disabled:opacity-40"
      >
        SET
      </button>
      <button
        onClick={onSetFree}
        disabled={busy}
        data-testid={`tenant-module-${mod.id}-free`}
        className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono bg-emerald-500/10 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/20 disabled:opacity-40 flex items-center gap-1"
        title="FREE for this tenant"
      >
        <Gift size={10} />
      </button>
      {mod.tenant_override && (
        <button
          onClick={onReset}
          disabled={busy}
          data-testid={`tenant-module-${mod.id}-reset`}
          className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border pal-mono flex items-center gap-1 ${
            isDark ? 'border-white/10 text-slate-400 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
        >
          <RotateCcw size={10} />
        </button>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
//                          REVENUE TAB
// ────────────────────────────────────────────────────────────────
function RevenueTab({ isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on plain async fetches. */
  useEffect(() => {
    setLoading(true);
    api.get('/billing/admin/revenue/overview')
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load revenue overview'))
      .finally(() => setLoading(false));
  }, []);
  /* eslint-enable */

  if (loading || !data) {
    return <div className="pal-card p-8 text-center opacity-60 text-sm">Computing revenue…</div>;
  }

  const k = data.kpis || {};
  const maxModuleArr = Math.max(1, ...(data.by_module || []).map((m) => m.arr));
  const maxTenant = Math.max(1, ...(data.top_tenants || []).map((t) => t.monthly));

  return (
    <div className="space-y-4" data-testid="pricing-revenue-tab">
      {/* ── KPI hero ── */}
      <div className="pal-card pal-grid-bg p-5" data-testid="revenue-kpi-strip">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={13} className="text-[var(--ve-violet-bright)]" />
          <span className="pal-bracket">[ANNUAL · RUN-RATE]</span>
          <span className="pal-mono text-[9px] opacity-50 ml-auto">
            FORWARD-LOOKING · CURRENT PRICING
          </span>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiTile
            label="ARR"
            value={fmtQar(k.total_arr)}
            sub={`MRR ${fmtQar(k.total_mrr)}`}
            accent="#D4AF37"
            isDark={isDark}
            testid="revenue-kpi-arr"
          />
          <KpiTile
            label="PAYING TENANTS"
            value={k.paying_tenants}
            sub={`of ${k.total_tenants} total`}
            accent="#10B981"
            isDark={isDark}
            testid="revenue-kpi-paying"
          />
          <KpiTile
            label="FREE TENANTS"
            value={k.free_tenants}
            sub="complimentary subs"
            accent="#F59E0B"
            isDark={isDark}
            testid="revenue-kpi-free"
          />
          <KpiTile
            label="CUSTOM PRICED"
            value={`${k.custom_priced_pct}%`}
            sub={`${k.custom_priced_tenants} of ${k.total_tenants}`}
            accent="#C084FC"
            isDark={isDark}
            testid="revenue-kpi-custom"
          />
          <KpiTile
            label="FREE HANDOUTS"
            value={k.lifetime_free_count}
            sub={`≈ ${fmtQar(k.lifetime_free_value_qar)} forgone`}
            accent="#EF4444"
            isDark={isDark}
            testid="revenue-kpi-free-handouts"
          />
        </div>
      </div>

      {/* ── ARR by module ── */}
      <div className="pal-card p-5" data-testid="revenue-by-module">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BarChart3 size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[ARR · BY MODULE]</span>
          </div>
          <span className="pal-mono text-[10px] opacity-60">
            {(data.by_module || []).length} ACTIVE
          </span>
        </div>
        {(data.by_module || []).length === 0 ? (
          <div className="text-sm opacity-60 italic py-3">
            No modules adopted yet. Once tenants pick modules at /pricing,
            their ARR contribution lands here.
          </div>
        ) : (
          <div className="space-y-2">
            {(data.by_module || []).map((m) => {
              const pct = (m.arr / maxModuleArr) * 100;
              return (
                <div
                  key={m.module_id}
                  className={`flex items-center gap-3 px-3 py-2 rounded border ${
                    isDark ? 'border-white/5 bg-[rgba(174,82,234,0.02)]' : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`revenue-module-row-${m.module_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`pal-mono text-[12px] font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {m.name}
                      </span>
                      {m.is_custom && (
                        <span className="pal-mono text-[8px] tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm border bg-emerald-500/10 text-emerald-500 border-emerald-500/40">
                          CUSTOM
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 dark:bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: 'linear-gradient(90deg, #D4AF37, #C084FC)',
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-right shrink-0 min-w-[120px]">
                    <div className="display text-base tabular-nums" style={{ color: '#D4AF37' }}>
                      {fmtQar(m.arr)}
                    </div>
                    <div className="pal-mono text-[9px] opacity-50">
                      {m.adopters} adopters · {fmtQar(m.mrr)}/mo
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Top-paying tenants ── */}
      <div className="pal-card p-5" data-testid="revenue-top-tenants">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 size={13} className="text-[var(--ve-violet-bright)]" />
            <span className="pal-bracket">[TOP · TENANTS]</span>
          </div>
          <span className="pal-mono text-[10px] opacity-60">
            TOP {(data.top_tenants || []).length} OF {data.all_tenants_count}
          </span>
        </div>
        {(data.top_tenants || []).length === 0 ? (
          <div className="text-sm opacity-60 italic py-3">
            No paying tenants yet.
          </div>
        ) : (
          <div className="space-y-1.5">
            {(data.top_tenants || []).map((t, i) => {
              const pct = (t.monthly / maxTenant) * 100;
              return (
                <div
                  key={t.tenant_id}
                  className={`flex items-center gap-3 px-3 py-2 rounded border ${
                    isDark ? 'border-white/5 bg-[rgba(174,82,234,0.02)]' : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`revenue-tenant-row-${t.tenant_id}`}
                >
                  <span className="pal-mono text-[10px] opacity-50 w-5 text-right">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`pal-mono text-[12px] font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {t.brand_name}
                      </span>
                      {t.is_custom_priced && (
                        <span
                          className="pal-mono text-[8px] tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm border"
                          style={{
                            backgroundColor: 'rgba(192,132,252,0.10)',
                            color: '#C084FC',
                            borderColor: 'rgba(192,132,252,0.40)',
                          }}
                        >
                          CUSTOM RATE
                        </span>
                      )}
                    </div>
                    <div className="pal-mono text-[9px] opacity-50">
                      {t.tenant_id} · {t.module_count} modules · {t.billing}
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-slate-200 dark:bg-white/5 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: t.is_custom_priced ? '#C084FC' : '#D4AF37',
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-right shrink-0 min-w-[120px]">
                    <div className="display text-base tabular-nums" style={{ color: '#D4AF37' }}>
                      {fmtQar(t.annual)}
                    </div>
                    <div className="pal-mono text-[9px] opacity-50">
                      {fmtQar(t.monthly)}/mo
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="pal-mono text-[10px] opacity-50 text-center">
        Last computed {new Date(data.generated_at).toLocaleString()} · all amounts in {data.currency_primary}
      </p>
    </div>
  );
}

function KpiTile({ label, value, sub, accent, isDark, testid }) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        isDark ? 'bg-[rgba(174,82,234,0.03)] border-white/10' : 'bg-slate-50 border-slate-200'
      }`}
      data-testid={testid}
    >
      <div className="pal-bracket text-[9px] truncate">[{label}]</div>
      <div className="display text-2xl mt-1 tabular-nums truncate" style={{ color: accent }}>
        {value}
      </div>
      <div className="pal-mono text-[9px] opacity-60 truncate mt-0.5">{sub}</div>
    </div>
  );
}
